/* eslint no-restricted-imports:0 */
import React, { useContext, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { isSpaceOverviewRoute } from '@confluence-classic/confluence-urls';

import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { usePageState } from '@confluence/page-context';
import {
	isStatusCodeError,
	isUnauthorizedError,
	ErrorDisplay,
	TransparentErrorBoundary,
	Attribution,
} from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import {
	DWELL_TIME_START_EXPERIENCE,
	getExperienceTracker, // eslint-disable-line no-restricted-imports
} from '@confluence/experience-tracker';
import { COMPANY_HUB, SPACE_INDEX } from '@confluence/named-routes';
import { useSearchSessionId } from '@confluence/search-session';

import DwellTimeComponent from './DwellTimeComponent';
import { DwellTimeQuery } from './DwellTimeQuery.graphql.js';
import { LastSuccessfulContentTracker } from './LastSuccessfulContentTracker.js';

// LastSuccessfulContentTracker needs to be created before other graphql queries
const lastContentTracker = new LastSuccessfulContentTracker();

export const DwellTime = () => {
	const { location, match, transitionId } = useContext(RoutesContext);
	const [{ contentId, spaceKey, contentType, contentIdLoading }] = usePageState();
	const [{ searchSessionId, additionalAnalytics }] = useSearchSessionId();
	const experienceTracker = getExperienceTracker();
	useEffect(() => {
		lastContentTracker.start();
		experienceTracker.start({ name: DWELL_TIME_START_EXPERIENCE });
		return () => {
			lastContentTracker.stop();
		};
		// This useEffect should only execute once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * @param {import("apollo-client").ApolloError} error
	 */
	const onDwellTimeQueryError = (error) => {
		if (
			isStatusCodeError(error, '404') || // Space not found
			isUnauthorizedError(error) // Insufficient permissions
		) {
			markErrorAsHandled(error);
		}
	};

	const { data, loading, error } = useQuery(DwellTimeQuery, {
		variables: {
			spaceKey,
		},
		onError: onDwellTimeQueryError,
		skip: !contentId || !spaceKey,
	});

	const spaceId = data?.space?.id;

	if (loading || !spaceId) {
		return null;
	}

	const isSpace =
		isSpaceOverviewRoute(location) ||
		// Company hub and SPACE_INDEX including its route (which matters here) is built on top of
		// space overview. Without this DwellTimeComponent wouldn't be able to
		// derive an analytics event source and an unhandled error
		// `Missing event.source` would occur.
		match?.name === COMPANY_HUB.name ||
		match?.name === SPACE_INDEX.name;

	return (
		<TransparentErrorBoundary attribution={Attribution.DISCO}>
			<DwellTimeComponent
				spaceId={data.space.id}
				contentId={contentId}
				contentIdLoading={contentIdLoading}
				contentType={contentType}
				isSpace={isSpace}
				experienceTracker={experienceTracker}
				lastContentTracker={lastContentTracker}
				searchSessionId={searchSessionId}
				isUserViewingOwnPersonalSpace={data?.user?.confluence?.space?.id === spaceId}
				spaceType={data?.space?.type}
				isInitial={transitionId === 0}
				additionalAnalytics={additionalAnalytics}
			/>
			{error && <ErrorDisplay error={error} />}
		</TransparentErrorBoundary>
	);
};

export default DwellTime;
